import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Newspaper } from "lucide-react";
import { useLang } from "@/i18n";
import { articles, trArticle } from "@/data/blog";

const L = {
  fr: { badge: "Le Blog I-LEASE", title: "Actualités & conseils leasing", subtitle: "Fiscalité, véhicules électriques, financement pro : nos guides pour la Belgique.", all: "Voir tous les articles", read: "Lire l'article" },
  nl: { badge: "De I-LEASE Blog", title: "Nieuws & leasingadvies", subtitle: "Fiscaliteit, elektrische voertuigen, pro-financiering: onze gidsen voor België.", all: "Alle artikels bekijken", read: "Lees het artikel" },
  en: { badge: "The I-LEASE Blog", title: "News & leasing insights", subtitle: "Taxation, electric vehicles, pro financing: our guides for Belgium.", all: "See all articles", read: "Read the article" },
};

export const BlogSection = () => {
  const { lang } = useLang();
  const t = L[lang] || L.fr;
  return (
    <section id="blog" className="relative py-24 sm:py-32 bg-[#0d2138] border-y border-white/5" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/30 px-4 py-1.5 mb-4">
              <Newspaper className="w-4 h-4 text-accent" strokeWidth={1.5} />
              <span className="text-sm text-accent">{t.badge}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold">{t.title}</h2>
            <p className="mt-4 text-muted-foreground text-lg">{t.subtitle}</p>
          </div>
          <Link to="/blog" data-testid="blog-see-all" className="inline-flex items-center gap-2 text-sm text-white border border-white/15 rounded-full px-5 py-2.5 hover:border-accent hover:text-accent transition-colors">
            {t.all} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {articles.map((a, i) => {
            const at = trArticle(a, lang);
            return (
            <motion.div key={a.slug}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.12 }}>
              <Link to={`/blog/${a.slug}`} data-testid={`blog-card-${a.slug}`}
                className="group block rounded-2xl overflow-hidden border border-white/10 bg-card hover:-translate-y-1.5 transition-transform duration-300 h-full">
                <div className="relative h-48 overflow-hidden">
                  <img src={a.cover} alt={at.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 text-[11px] uppercase tracking-wider bg-accent text-white rounded-full px-3 py-1">{at.category}</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <span>{new Date(a.date).toLocaleDateString(lang === "fr" ? "fr-BE" : lang === "nl" ? "nl-BE" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span>·</span><span>{a.readTime}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold leading-snug group-hover:text-accent transition-colors">{at.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">{at.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent">
                    {t.read} <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
